/**
 * In-App Notifications Hook
 * - Fetches notifications from API
 * - Real-time updates via WebSocket
 * - Unread count badge
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

export interface Notification {
  id: number;
  title: string;
  body: string;
  icon?: string;
  url?: string;
  type: string;
  reference_id?: number;
  is_read: boolean;
  created_at: string;
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const intervalRef = useRef<any>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications', { params: { limit: 50 } });
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch {}
  }, []);

  const markRead = useCallback(async (id: number) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  }, []);

  const deleteNotification = useCallback(async (id: number) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => {
        const n = prev.find(x => x.id === id);
        if (n && !n.is_read) setUnreadCount(c => Math.max(0, c - 1));
        return prev.filter(x => x.id !== id);
      });
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Poll every 30s as a fallback (WS is primary)
    intervalRef.current = setInterval(fetchNotifications, 30000);
    // Refresh when tab becomes visible
    const vis = () => { if (document.visibilityState === 'visible') fetchNotifications(); };
    document.addEventListener('visibilitychange', vis);
    return () => { clearInterval(intervalRef.current); document.removeEventListener('visibilitychange', vis); };
  }, [fetchNotifications]);

  return { notifications, unreadCount, loading, fetchNotifications, markRead, markAllRead, deleteNotification };
}
